import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getDismissed,
  getExpanded,
  setDismissed as writeDismissed,
  setExpanded as writeExpanded,
} from "./gettingStartedStorage";

export type TourTrack = "partners" | "invite" | "referral" | "example";

interface GettingStartedContextValue {
  /** Panel is hidden entirely (user tapped "Finish getting started"). */
  dismissed: boolean;
  /** Accordion open/closed state. */
  expanded: boolean;
  setExpanded: (value: boolean) => void;
  /** Confirm-then-dismiss flow. */
  finish: () => void;
  /** Which walkthrough track is currently active, if any. */
  activeTrack: TourTrack | null;
  startTrack: (track: TourTrack) => void;
  endTrack: () => void;
}

const GettingStartedContext = createContext<GettingStartedContextValue | undefined>(
  undefined
);

export function GettingStartedProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;

  const [dismissed, setDismissedState] = useState<boolean>(() => getDismissed(userId));
  const [expanded, setExpandedState] = useState<boolean>(() => getExpanded(userId));
  const [activeTrack, setActiveTrack] = useState<TourTrack | null>(null);

  // When the signed-in user changes (login/logout/switch), re-hydrate from that
  // user's scoped storage. Otherwise a previous user's state would bleed over.
  useEffect(() => {
    setDismissedState(getDismissed(userId));
    setExpandedState(getExpanded(userId));
    setActiveTrack(null);
  }, [userId]);

  const setExpanded = useCallback(
    (value: boolean) => {
      setExpandedState(value);
      writeExpanded(userId, value);
    },
    [userId]
  );

  const finish = useCallback(() => {
    setDismissedState(true);
    writeDismissed(userId, true);
    setActiveTrack(null);
  }, [userId]);

  const startTrack = useCallback((track: TourTrack) => {
    setActiveTrack(track);
  }, []);

  const endTrack = useCallback(() => {
    setActiveTrack(null);
  }, []);

  const value = useMemo<GettingStartedContextValue>(
    () => ({
      dismissed,
      expanded,
      setExpanded,
      finish,
      activeTrack,
      startTrack,
      endTrack,
    }),
    [dismissed, expanded, setExpanded, finish, activeTrack, startTrack, endTrack]
  );

  return (
    <GettingStartedContext.Provider value={value}>
      {children}
    </GettingStartedContext.Provider>
  );
}

export function useGettingStarted(): GettingStartedContextValue {
  const ctx = useContext(GettingStartedContext);
  if (!ctx) {
    throw new Error("useGettingStarted must be used within GettingStartedProvider");
  }
  return ctx;
}
